import React, { useCallback, useEffect, useMemo, useState } from "react";
import QmsDocumentPreviewPane from "./QmsDocumentPreviewPane";
import QmsModalPortal from "./QmsModalPortal";
import {
  acknowledgeMyLearningDocument,
  fetchMyLearningDocumentHub,
} from "./qmsService";
import {
  getApiTable,
  getApiTableRow,
  isApiSuccess,
  apiMessage,
} from "./qmsApiUtils";
import {
  buildMyLearningCertificateHtml,
  buildMyLearningCertificatePrintDocument,
  downloadBlobAsFile,
  formatMyLearningCertificateDate,
  getMyLearningCertificateDocumentName,
  getMyLearningDocumentCode,
  getMyLearningQuizAttemptLimitMessage,
  getMyLearningQuizAttemptState,
  getMyLearningUnifiedStatusMeta,
  isMyLearningCompleted,
  renderMyLearningCertificatePdfBlob,
} from "./qmsMyLearningsUtils";

function groupQuizQuestions(questions, options) {
  const map = new Map();
  (questions || []).forEach((q) => {
    const qid = String(q.QuestionId || "");
    map.set(qid, {
      questionId: qid,
      question: String(q.Question || ""),
      options: [],
    });
  });
  (options || []).forEach((opt) => {
    const qid = String(opt.QuestionId || "");
    const entry = map.get(qid);
    if (!entry) return;
    entry.options.push({
      optionId: String(opt.OptionId || ""),
      text: String(opt.OptionText || ""),
      serial: Number(opt.SerialNumber) || 0,
    });
  });
  return [...map.values()].map((item) => ({
    ...item,
    options: item.options.sort((a, b) => a.serial - b.serial),
  }));
}

function QmsMyLearningsWorkspaceModal({
  open,
  onClose,
  row,
  userData,
  staffDisplayName,
  onCompleted,
  notifyError,
  notifySuccess,
}) {
  const [activePanel, setActivePanel] = useState("preview");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hub, setHub] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [certificateDownloading, setCertificateDownloading] = useState(false);

  const documentId = String(row?.DocumentId || "").trim();

  const loadHub = useCallback(async () => {
    if (!documentId || !userData) return;
    setLoading(true);
    try {
      const response = await fetchMyLearningDocumentHub(userData, documentId);
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Failed to load document.");
      }
      setHub(response);
      setSelectedOptions({});
    } catch (error) {
      notifyError?.(error?.message || "Failed to load document.");
      setHub(null);
    } finally {
      setLoading(false);
    }
  }, [documentId, notifyError, userData]);

  useEffect(() => {
    if (!open || !documentId) return;
    setActivePanel("preview");
    setCertificateDownloading(false);
    loadHub();
  }, [open, documentId, loadHub]);

  const detail = useMemo(
    () => getApiTableRow(hub, "MyLearningDocument") || row || {},
    [hub, row],
  );

  const associatedDocs = useMemo(
    () => getApiTable(hub, "AssociatedDocuments"),
    [hub],
  );

  const certificateTemplate = useMemo(
    () => getApiTableRow(hub, "CertificateTemplate")?.TemplateContent || "",
    [hub],
  );

  const quizQuestions = useMemo(
    () =>
      groupQuizQuestions(
        getApiTable(hub, "QuizQuestions"),
        getApiTable(hub, "QuizOptions"),
      ),
    [hub],
  );

  const quizMeta = useMemo(() => getApiTableRow(hub, "QuizQuestions") || {}, [hub]);

  const completed = isMyLearningCompleted(detail);
  const quizRequired = Number(detail?.IsQuizApplicable) === 1;
  const quizAttemptState = useMemo(
    () => getMyLearningQuizAttemptState(detail, quizMeta),
    [detail, quizMeta],
  );
  const quizAttemptMessage = getMyLearningQuizAttemptLimitMessage(quizAttemptState);
  const quizLockedByAttempts = quizRequired && !completed && quizAttemptState.limitReached;
  const documentCode = getMyLearningDocumentCode(detail);
  const documentTitle = String(
    detail?.DocumentTitleWithExt || detail?.DocumentTitle || row?.DocumentTitle || "Document",
  );
  const certificateDocumentName = getMyLearningCertificateDocumentName(detail);
  const statusMeta = getMyLearningUnifiedStatusMeta(detail);

  const answeredCount = useMemo(
    () => quizQuestions.filter((q) => selectedOptions[q.questionId]).length,
    [quizQuestions, selectedOptions],
  );

  const quizProgress = quizQuestions.length
    ? Math.round((answeredCount / quizQuestions.length) * 100)
    : 0;

  const visiblePanels = useMemo(() => {
    const panels = [
      {
        id: "preview",
        label: quizRequired ? "Learning" : "Preview",
        icon: quizRequired ? "fas fa-book-reader" : "fas fa-eye",
      },
      { id: "associated", label: "Associated", icon: "fas fa-link" },
    ];
    if (completed) {
      panels.push({ id: "certificate", label: "Certificate", icon: "fas fa-award" });
    }
    return panels;
  }, [completed, quizRequired]);

  const certificateStaffName = useMemo(() => {
    const fromProp = String(staffDisplayName || "").trim();
    if (fromProp) return fromProp;
    const fromDetail = String(detail?.StaffName || detail?.FullName || "").trim();
    if (fromDetail) return fromDetail;
    return [userData?.FirstName, userData?.LastName].filter(Boolean).join(" ").trim();
  }, [detail, staffDisplayName, userData]);

  const certificateHtml = useMemo(
    () =>
      buildMyLearningCertificateHtml(certificateTemplate, {
        staffName: certificateStaffName,
        documentTitle: certificateDocumentName,
        documentCode,
        completedOn: formatMyLearningCertificateDate(
          detail?.AcknowledgedAt ||
            detail?.CompletedOn ||
            detail?.CompletedDate ||
            detail?.CompletedDateTime,
        ),
        versionNumber: detail?.VersionNumber,
        organizationName: userData?.OrganizationName || "",
      }),
    [
      certificateDocumentName,
      certificateStaffName,
      certificateTemplate,
      detail,
      documentCode,
      userData,
    ],
  );

  const certificatePreviewDocument = useMemo(
    () => buildMyLearningCertificatePrintDocument(certificateHtml),
    [certificateHtml],
  );

  const handleDownloadCertificatePdf = useCallback(async () => {
    if (!certificateHtml || certificateDownloading) return;
    setCertificateDownloading(true);
    try {
      const blob = await renderMyLearningCertificatePdfBlob(certificateHtml);
      const safeName = String(certificateDocumentName || "Certificate")
        .replace(/[^\w.-]+/g, "_")
        .slice(0, 80);
      downloadBlobAsFile(blob, `${safeName}_Certificate.pdf`);
      notifySuccess?.("Certificate downloaded.");
    } catch (error) {
      notifyError?.(error?.message || "Failed to download certificate PDF.");
    } finally {
      setCertificateDownloading(false);
    }
  }, [
    certificateDocumentName,
    certificateDownloading,
    certificateHtml,
    notifyError,
    notifySuccess,
  ]);

  const handleSelectOption = (questionId, optionId) => {
    if (completed || submitting || quizLockedByAttempts) return;
    setSelectedOptions((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleAcknowledge = async () => {
    if (!documentId || completed) return;
    setSubmitting(true);
    try {
      const response = await acknowledgeMyLearningDocument(userData, { documentId });
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Acknowledgement failed.");
      }
      notifySuccess?.(apiMessage(response) || "Document acknowledged.");
      await loadHub();
      onCompleted?.();
    } catch (error) {
      notifyError?.(error?.message || "Acknowledgement failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!documentId || !quizRequired) return;
    if (quizLockedByAttempts) {
      notifyError?.(
        quizAttemptMessage ||
          "You have reached the maximum number of quiz attempts for this document.",
      );
      return;
    }
    const unanswered = quizQuestions.filter((q) => !selectedOptions[q.questionId]);
    if (unanswered.length > 0) {
      notifyError?.("Please answer all quiz questions before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const userOptions = quizQuestions
        .map((q) => selectedOptions[q.questionId])
        .filter(Boolean)
        .join(",");
      const response = await acknowledgeMyLearningDocument(userData, {
        documentId,
        userOptions,
        quizId: String(quizMeta?.QuizzId || quizMeta?.QuizId || ""),
      });
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Quiz submission failed.");
      }
      notifySuccess?.(apiMessage(response) || "Quiz submitted.");
      await loadHub();
      onCompleted?.();
    } catch (error) {
      notifyError?.(error?.message || "Quiz submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuizRail = () => {
    if (quizQuestions.length === 0) {
      return (
        <div className="qms-my-learnings-quiz qms-my-learnings-quiz--empty">
          <p className="text-muted mb-0">No quiz questions are configured for this document.</p>
        </div>
      );
    }

    return (
      <div className="qms-my-learnings-quiz">
        <div className="qms-my-learnings-quiz__header">
          <div className="qms-my-learnings-quiz__header-top">
            <div>
              <h6 className="qms-my-learnings-quiz__title">Knowledge check</h6>
              <p className="qms-my-learnings-quiz__subtitle">
                Read the document on the left, then answer all questions to complete this learning.
              </p>
            </div>
            <span className="qms-my-learnings-quiz__pass-badge">
              Pass {quizMeta?.PassingPercentage ?? "—"}%
            </span>
          </div>
          {!quizAttemptState.unlimited ? (
            <p
              className={`qms-my-learnings-quiz__attempt-meta${
                quizLockedByAttempts ? " is-limit-reached" : ""
              }`}
            >
              {quizAttemptMessage}
            </p>
          ) : null}
          {quizLockedByAttempts ? (
            <div className="qms-my-learnings-quiz__completed-banner is-limit-reached">
              <i className="fas fa-ban" aria-hidden="true" />
              Attempt limit reached
              {detail?.UserPercentage != null ? ` — last score ${detail.UserPercentage}%` : ""}
            </div>
          ) : !completed ? (
            <div className="qms-my-learnings-quiz__progress-wrap">
              <div className="qms-my-learnings-quiz__progress-meta">
                <span>
                  {answeredCount} of {quizQuestions.length} answered
                </span>
                <span>{quizProgress}%</span>
              </div>
              <div
                className="qms-my-learnings-quiz__progress-bar"
                role="progressbar"
                aria-valuenow={quizProgress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span style={{ width: `${quizProgress}%` }} />
              </div>
            </div>
          ) : (
            <div className="qms-my-learnings-quiz__completed-banner">
              <i className="fas fa-check-circle" aria-hidden="true" />
              Quiz passed
              {detail?.UserPercentage != null ? ` — ${detail.UserPercentage}%` : ""}
            </div>
          )}
        </div>

        <div className="qms-my-learnings-quiz__questions">
          {quizQuestions.map((question, index) => {
            const isAnswered = Boolean(selectedOptions[question.questionId]);
            return (
              <article
                key={question.questionId}
                className={`qms-my-learnings-quiz__card${
                  isAnswered ? " is-answered" : ""
                }`}
              >
                <div className="qms-my-learnings-quiz__card-head">
                  <span className="qms-my-learnings-quiz__q-num">Q{index + 1}</span>
                  <p className="qms-my-learnings-quiz__q-text">{question.question}</p>
                </div>
                <div className="qms-my-learnings-quiz__options">
                  {question.options.map((option, optionIndex) => {
                    const selected =
                      selectedOptions[question.questionId] === option.optionId;
                    return (
                      <button
                        key={option.optionId}
                        type="button"
                        className={`qms-my-learnings-quiz__option${
                          selected ? " is-selected" : ""
                        }`}
                        disabled={completed || submitting || quizLockedByAttempts}
                        onClick={() =>
                          handleSelectOption(question.questionId, option.optionId)
                        }
                      >
                        <span className="qms-my-learnings-quiz__option-letter">
                          {String.fromCharCode(65 + optionIndex)}
                        </span>
                        <span className="qms-my-learnings-quiz__option-text">{option.text}</span>
                        {selected ? (
                          <i className="fas fa-check qms-my-learnings-quiz__option-check" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </article>
            );
          })}
        </div>

        {!completed && !quizLockedByAttempts ? (
          <div className="qms-my-learnings-quiz__footer">
            <span className="qms-my-learnings-quiz__footer-hint">
              {answeredCount === quizQuestions.length
                ? "All questions answered — ready to submit"
                : `${quizQuestions.length - answeredCount} question(s) remaining`}
            </span>
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting || answeredCount !== quizQuestions.length}
              onClick={handleSubmitQuiz}
            >
              {submitting ? "Submitting…" : "Submit quiz"}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPreviewPanel = () => {
    if (quizRequired) {
      return (
        <div className="qms-my-learnings-modal__split">
          <div className="qms-my-learnings-modal__split-preview">
            <div className="qms-my-learnings-modal__split-label">
              <i className="fas fa-file-alt" aria-hidden="true" />
              Document preview
            </div>
            <div className="qms-doc-workspace__preview-pane">
              <QmsDocumentPreviewPane
                active
                documentId={
                  detail?.DocumentId ||
                  detail?.DocumentIdRef ||
                  row?.DocumentId ||
                  row?.DocumentIdRef ||
                  ""
                }
                documentUrl={detail?.DocumentURL || row?.DocumentURL}
                documentExtension={detail?.DocumentExtension || row?.DocumentExtension}
                documentTitle={documentTitle}
              />
            </div>
          </div>
          <aside className="qms-my-learnings-modal__split-quiz">{renderQuizRail()}</aside>
        </div>
      );
    }

    return (
      <div className="qms-my-learnings-modal__preview">
        <div className="qms-doc-workspace__preview-pane">
          <QmsDocumentPreviewPane
            active
            documentId={
              detail?.DocumentId ||
              detail?.DocumentIdRef ||
              row?.DocumentId ||
              row?.DocumentIdRef ||
              ""
            }
            documentUrl={detail?.DocumentURL || row?.DocumentURL}
            documentExtension={detail?.DocumentExtension || row?.DocumentExtension}
            documentTitle={documentTitle}
          />
        </div>
        {!completed ? (
          <div className="qms-my-learnings-modal__action-bar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting}
              onClick={handleAcknowledge}
            >
              Acknowledge reading
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPanelContent = () => {
    if (loading) {
      return <div className="qms-my-learnings-modal__loading">Loading document…</div>;
    }

    if (activePanel === "preview") {
      return renderPreviewPanel();
    }

    if (activePanel === "associated") {
      return (
        <div className="qms-doc-workspace__panel qms-my-learnings-modal__associated">
          {associatedDocs.length === 0 ? (
            <p className="text-muted mb-0">No associated documents.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Version</th>
                    <th>Published</th>
                  </tr>
                </thead>
                <tbody>
                  {associatedDocs.map((doc) => (
                    <tr key={String(doc.DocumentIdRef || doc.DocumentTitle)}>
                      <td>
                        {doc.DocumentURL ? (
                          <a href={doc.DocumentURL} target="_blank" rel="noopener noreferrer">
                            {doc.DocumentTitle}
                          </a>
                        ) : (
                          doc.DocumentTitle
                        )}
                      </td>
                      <td>{doc.DocumentTypeAbberiviation || doc.DocumentType || "—"}</td>
                      <td>{doc.VersionNumber ?? "—"}</td>
                      <td>{doc.PublishedDateTime || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (activePanel === "certificate" && completed) {
      return (
        <div className="qms-doc-workspace__panel qms-my-learnings-modal__certificate">
          <div className="qms-my-learnings-modal__certificate-toolbar">
            <p className="text-muted mb-0">
              Your completion certificate is ready. Preview below, then download as PDF.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              disabled={certificateDownloading || !certificateHtml}
              onClick={() => void handleDownloadCertificatePdf()}
            >
              <i className="fas fa-file-pdf me-1" aria-hidden="true" />
              {certificateDownloading ? "Downloading…" : "Download PDF"}
            </button>
          </div>
          <div className="qms-my-learnings-modal__certificate-html">
            <iframe
              title="Certificate of Completion"
              className="qms-my-learnings-modal__certificate-frame"
              srcDoc={certificatePreviewDocument}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  if (!open || !row) return null;

  const isSplitPreview = quizRequired && activePanel === "preview";

  return (
    <QmsModalPortal>
      <div className="qms-document-library">
        <div
          className="modal fade show d-block qms-doc-workspace-modal"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="My learning document"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          onClick={onClose}
        >
          <div
            className="modal-dialog qms-doc-workspace-modal__dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content qms-doc-workspace-modal__content">
              <div className="qms-doc-workspace-modal__header">
                <div className="qms-doc-workspace-modal__header-main">
                  <div className="qms-doc-workspace-modal__icon" aria-hidden="true">
                    <i className="fas fa-graduation-cap" />
                  </div>
                  <div>
                    <h5 className="qms-doc-workspace-modal__title">{documentTitle}</h5>
                    <div className="qms-doc-workspace-modal__code-row">
                      {documentCode ? (
                        <div className="qms-doc-workspace-modal__code">{documentCode}</div>
                      ) : null}
                      {detail?.PublishedOn ? (
                        <span className="qms-my-learnings-modal__published">
                          Published {detail.PublishedOn}
                        </span>
                      ) : null}
                      <span className={`qms-my-learnings-badge is-${statusMeta.tone}`}>
                        {statusMeta.label}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="qms-doc-workspace-modal__header-actions">
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={onClose}
                  />
                </div>
              </div>

              <div className="qms-doc-workspace-modal__body">
                <nav
                  className="qms-doc-workspace-modal__sidebar"
                  aria-label="Document sections"
                >
                  <ul className="qms-doc-workspace-modal__nav">
                    {visiblePanels.map((panel) => {
                      const isActive = activePanel === panel.id;
                      return (
                        <li key={panel.id}>
                          <button
                            type="button"
                            className={`qms-doc-workspace-modal__nav-item${
                              isActive ? " is-active" : ""
                            }`}
                            aria-current={isActive ? "page" : undefined}
                            onClick={() => setActivePanel(panel.id)}
                          >
                            <i className={panel.icon} aria-hidden="true" />
                            <span>{panel.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                <div
                  className={`qms-doc-workspace-modal__main${
                    isSplitPreview || (!quizRequired && activePanel === "preview")
                      ? " is-preview"
                      : ""
                  }${isSplitPreview ? " is-learning-split" : ""}`}
                >
                  {renderPanelContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" />
      </div>
    </QmsModalPortal>
  );
}

export default QmsMyLearningsWorkspaceModal;
